import {AfterViewInit, Component, ElementRef, NgZone, OnInit, ViewChild} from '@angular/core';
import {AuthenticationService} from "../../../../service/authentication-service";
import {Router} from "@angular/router";
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {ErrorMessageEnum} from "../../../../shared/enums/error-message.enum";
import {AuthenticationResponse} from "../../../../shared/model/response/auth/authentication-response.model";
import {AccessService} from "../../../../service/api/access.service";
import {ApiResponse} from "../../../../shared/model/response/ApiResponse";
import {MessageToastService} from "../../../../service/message-toast.service";
import {PrivilegeService} from "../../../../service/privilege.service";
import {environment} from "../../../../../environments/environment";

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  standalone: false
})
export class LoginComponent implements OnInit, AfterViewInit {

  loginForm: FormGroup;
  errorObject = {phoneNumber: "", password: ""}

  @ViewChild('googleButton') googleButton?: ElementRef<HTMLDivElement>

  private googleLoginStarted = false;

  constructor(private authService: AuthenticationService,
              private acessoService: AccessService,
              private privilegeService: PrivilegeService,
              private toastMessageService: MessageToastService,
              private ngZone: NgZone,
              private router: Router) {
  }

  ngOnInit(): void {
    this.loginForm = new FormGroup<any>({
      "phoneNumber": new FormControl(null, [Validators.required, Validators.pattern(/^\d{9}$/)]),
      "password": new FormControl(null, Validators.required)
    })
  }

  ngAfterViewInit(): void {
    this.renderGoogleButton()
  }

  private renderGoogleButton() {
    this.loadGoogleScript().then(() => {
      const googleIdentity = (window as GoogleIdentityWindow).google
      const container = this.googleButton?.nativeElement
      if (!googleIdentity?.accounts?.id || !container) {
        return
      }
      googleIdentity.accounts.id.initialize({
        client_id: environment.googleClientId,
        callback: (response) => this.ngZone.run(() => this.onGoogleCredential(response.credential))
      })
      googleIdentity.accounts.id.renderButton(container, {
        theme: 'outline',
        size: 'large',
        shape: 'rectangular',
        text: 'continue_with',
        width: 320
      })
    }).catch(() => undefined)
  }

  private loadGoogleScript(): Promise<void> {
    if ((window as GoogleIdentityWindow).google?.accounts?.id) {
      return Promise.resolve()
    }
    const existing = document.getElementById('google-identity-script')
    if (existing) {
      return new Promise((resolve, reject) => {
        existing.addEventListener('load', () => resolve(), {once: true})
        existing.addEventListener('error', () => reject(), {once: true})
      })
    }
    return new Promise((resolve, reject) => {
      const script = document.createElement('script')
      script.id = 'google-identity-script'
      script.src = 'https://accounts.google.com/gsi/client'
      script.async = true
      script.onload = () => resolve()
      script.onerror = () => reject()
      document.head.appendChild(script)
    })
  }

  private onGoogleCredential(idToken: string) {
    if (!idToken || this.googleLoginStarted) {
      return
    }
    this.googleLoginStarted = true
    this.acessoService.googleLogin(idToken).subscribe({
      next: (token: AuthenticationResponse) => {
        this.authService.setSession(token.accessToken, token.refreshToken)
        this.privilegeService.updateRole()
        this.router.navigate(['/perfil']).then()
      },
        error: (apiError: ApiResponse<any>) => {
          this.googleLoginStarted = false
          this.toastMessageService.error(apiError?.message)
        }
    })
  }

  /**
   * User credentials login
   */
  onLogin() {

    //validate inputs on frontend
    if (this.loginForm.invalid) {
      this.validateFieldsLocally()
    } else {

      //call backend service
      this.acessoService.login(this.loginForm.value).subscribe({
        next: (token: AuthenticationResponse) => {
          this.authService.setSession(token.accessToken, token.refreshToken)
          this.privilegeService.updateRole()
          this.router.navigate(["/"]).then()
        },

        error: (apiError: ApiResponse<any>) => {

          if (apiError?.formErrors) {
            this.errorObject.phoneNumber = apiError.formErrors.get('phoneNumber');
            this.errorObject.password = apiError.formErrors.get('password');
          }

          this.toastMessageService.error(apiError?.message)
        }
      })
    }
  }

  /**
   * Validate the fields
   */
  //validate the field at the frontend level
  validateFieldsLocally() {
    const phone = this.loginForm.get("phoneNumber")
    const isPasswordRequired = this.loginForm.hasError("required", "password")

    if (phone?.hasError("required")) {
      this.errorObject.phoneNumber = ErrorMessageEnum.requiredField
    } else if (phone?.hasError("pattern")) {
      this.errorObject.phoneNumber = ErrorMessageEnum.invalidPhone
    }
    this.errorObject.password = (isPasswordRequired) ? ErrorMessageEnum.requiredField : ""
  }
}

interface GoogleIdentityWindow extends Window {
  google?: {
    accounts: {
      id: {
        initialize: (config: {client_id: string, callback: (response: {credential: string}) => void}) => void
        renderButton: (parent: HTMLElement, options: Record<string, string | number>) => void
      }
    }
  }
}
